import React, { useState, useEffect, useRef } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
  getClientsByOrganisation,
  getDefaultClientByOrganisation,
  toggleBlockClient,
} from "../../Services/ClientApi";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ClientDetailModal from "./ClientDetailModal";

const ClientSelector = ({
  organisation,
  selectedClient,
  onClientSelect,
  calcActive,
  searchBuffer,
  onActivateCalc,
  onDeactivateCalc,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredClients, setFilteredClients] = useState([]);
  const dropdownRef = useRef(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const hasCredit = Number(selectedClient?.credit) > 0;
  const isCustomClient = !!(selectedClient && !selectedClient.isDefault);

  // Load clients on mount or organisation change
  useEffect(() => {
    if (organisation) {
      loadClients();
    }
  }, [organisation]);

  // Filter clients when search term changes
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = clients.filter(
        (client) =>
          client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.code?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients);
    }
  }, [searchTerm, clients]);

  // Mirror the shared on-screen Calculator's buffer while it's bound to
  // this input, so typing on it drives the same client search as native input
  useEffect(() => {
    if (calcActive) {
      setSearchTerm(searchBuffer || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBuffer, calcActive]);

  // Close dropdown when clicking outside. Guarded by a ref (rather than
  // depending on showDropdown directly) so the listener — registered once —
  // still reads the latest open/closed state without re-subscribing. Without
  // this guard, the handler fired on EVERY outside click (e.g. tapping the
  // on-screen keypad rendered elsewhere on the page) and unconditionally
  // deactivated the shared calculator binding, wiping out "clientSearch" or
  // "productSearch" a moment before the keypad's own click handler ran.
  const showDropdownRef = useRef(showDropdown);
  useEffect(() => {
    showDropdownRef.current = showDropdown;
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showDropdownRef.current) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        if (onDeactivateCalc) onDeactivateCalc();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const response = await getClientsByOrganisation(organisation);
      if (response.data.success && response.data.data) {
        setClients(response.data.data);
        setFilteredClients(response.data.data);
      }
    } catch (error) {
      console.error("Error loading clients:", error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const selectClient = (client) => {
    onClientSelect(client);
    setShowDropdown(false);
    setSearchTerm("");
    if (onDeactivateCalc) onDeactivateCalc();
    toast.success(`Client sélectionné: ${client.name}`);
  };

  const handleClientSelect = async (client) => {
    if (!client.isBlocked) {
      selectClient(client);
      return;
    }

    const result = await Swal.fire({
      title: "Client bloqué",
      text: `${client.name} est bloqué et ne peut pas passer de commande. Voulez-vous le débloquer ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Débloquer le client",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await toggleBlockClient(client.id || client._id);
      if (res.data.success) {
        toast.success(`${client.name} a été débloqué.`);
        loadClients();
        selectClient(res.data.data);
      } else {
        toast.error("Erreur lors du déblocage du client.");
      }
    } catch (error) {
      console.error("Error unblocking client:", error);
      toast.error("Erreur lors du déblocage du client.");
    }
  };

  const handleReset = async (e) => {
    e.stopPropagation();
    setResetting(true);
    try {
      const response = await getDefaultClientByOrganisation(organisation);
      if (response.data.success && response.data.data) {
        onClientSelect(response.data.data);
      } else {
        onClientSelect(null);
      }
      toast.success("Client réinitialisé");
    } catch (error) {
      console.error("Error resetting client:", error);
      toast.error("Erreur lors de la réinitialisation du client");
      onClientSelect(null);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      className="client-selector"
      style={{ position: "relative", marginBottom: "10px" }}
      ref={dropdownRef}
    >
      {isCustomClient ? (
        <div
          className="d-flex align-items-stretch justify-content-between"
          style={{
            backgroundColor: hasCredit ? "#fdecea" : "#f8f9fa",
            borderRadius: "4px",
            border: hasCredit ? "1px solid #dc3545" : "1px solid #e0e0e0",
          }}
        >
          <div
            className="d-flex align-items-center flex-grow-1"
            style={{ padding: "8px 12px", cursor: "pointer" }}
            onClick={() => setDetailOpen(true)}
            title="Voir détails client"
          >
            <i
              className="fa fa-user mr-2"
              style={{ color: hasCredit ? "#c62828" : "#6c757d" }}
            ></i>
            <span className="badge badge-info mr-2">
              {selectedClient.code}
            </span>
            <span style={{ fontWeight: 600 }}>{selectedClient.name}</span>
            {hasCredit && (
              <span
                className="badge ml-2"
                style={{
                  fontSize: "10px",
                  backgroundColor: "#dc3545",
                  color: "#fff",
                }}
                title="Crédit en cours"
              >
                Crédit: {Number(selectedClient.credit).toFixed(3)} TND
              </span>
            )}
          </div>
          <div
            title="Réinitialiser (client par défaut)"
            onClick={handleReset}
            className="d-flex align-items-center justify-content-center"
            style={{
              padding: "0 14px",
              cursor: "pointer",
              borderLeft: hasCredit ? "1px solid #f1948a" : "1px solid #e0e0e0",
              color: "#c62828",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "rgba(198,40,40,0.08)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            {resetting ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <i className="fas fa-times" style={{ fontSize: "14px" }} />
            )}
          </div>
        </div>
      ) : (
        <div
          className="d-flex align-items-center justify-content-between"
          style={{
            padding: "8px 12px",
            backgroundColor: hasCredit ? "#fdecea" : "#f8f9fa",
            borderRadius: "4px",
            border: hasCredit ? "1px solid #dc3545" : "1px solid #e0e0e0",
            cursor: "pointer",
          }}
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <div className="d-flex align-items-center">
            <i
              className="fa fa-user mr-2"
              style={{ color: hasCredit ? "#c62828" : "#6c757d" }}
            ></i>
            <span style={{ fontWeight: "bold", marginRight: "10px" }}>
              Client:
            </span>
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : selectedClient ? (
              <div className="d-flex align-items-center">
                <span>{selectedClient.name}</span>
                <span
                  className="badge badge-warning ml-2"
                  style={{ fontSize: "10px" }}
                >
                  Par défaut
                </span>
                {hasCredit && (
                  <span
                    className="badge ml-2"
                    style={{
                      fontSize: "10px",
                      backgroundColor: "#dc3545",
                      color: "#fff",
                    }}
                    title="Crédit en cours"
                  >
                    Crédit: {Number(selectedClient.credit).toFixed(3)} TND
                  </span>
                )}
              </div>
            ) : (
              <span className="text-muted">Sélectionner un client</span>
            )}
          </div>
          <i
            className={`fa fa-chevron-${showDropdown ? "up" : "down"}`}
            style={{ fontSize: "12px", color: "#6c757d" }}
          ></i>
        </div>
      )}

      {showDropdown && !isCustomClient && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: "white",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            marginTop: "4px",
          }}
        >
          <div className="p-2">
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Rechercher un client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => {
                e.stopPropagation();
                if (onActivateCalc) onActivateCalc(searchTerm);
              }}
              autoFocus
              style={
                calcActive
                  ? { backgroundColor: "#fff3ec", borderColor: "#fd683e" }
                  : undefined
              }
            />
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {filteredClients.length === 0 ? (
                <div className="text-center text-muted p-2">
                  Aucun client trouvé
                </div>
              ) : (
                filteredClients.map((client) => (
                  <div
                    key={client.id || client._id}
                    className="client-item p-2"
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor:
                        selectedClient?.id === (client.id || client._id)
                          ? "#e3f2fd"
                          : "transparent",
                    }}
                    onClick={() => handleClientSelect(client)}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                    onMouseLeave={(e) => {
                      if (selectedClient?.id !== (client.id || client._id)) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>{client.name}</div>
                      <div className="d-flex align-items-center">
                        {Number(client.credit) > 0 && (
                          <span
                            className="badge mr-1"
                            style={{
                              fontSize: "10px",
                              backgroundColor: "#dc3545",
                              color: "#fff",
                            }}
                          >
                            {Number(client.credit).toFixed(3)} TND
                          </span>
                        )}
                        {client.isDefault && (
                          <span
                            className="badge badge-warning"
                            style={{ fontSize: "10px" }}
                          >
                            Defaut
                          </span>
                        )}
                        {client.isBlocked && (
                          <span
                            className="badge ml-1"
                            style={{
                              fontSize: "10px",
                              backgroundColor: "#721c24",
                              color: "#fff",
                            }}
                          >
                            Bloqué
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      <ClientDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        clientId={selectedClient?.id || selectedClient?._id}
      />
    </div>
  );
};

export default ClientSelector;
